import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Building2, ArrowLeft, Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import authService from '../services/auth';
import LanguageToggle from '../components/LanguageToggle';
import PhoneInput from '../components/common/PhoneInput';

// Searchable Dropdown Component
interface DropdownOption {
  value: string;
  label: string;
}

interface SearchableDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  icon
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'
        }`}
      >
        <div className="flex items-center">
          {icon && <span className="mr-2">{icon}</span>}
          <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                {option.label}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-4 py-2 text-gray-500 text-center">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Country data
const countryCodes: DropdownOption[] = [
  { value: '+965', label: '+965 (Kuwait)' },
  { value: '+966', label: '+966 (Saudi Arabia)' },
  { value: '+971', label: '+971 (UAE)' },
  { value: '+974', label: '+974 (Qatar)' },
  { value: '+973', label: '+973 (Bahrain)' },
  { value: '+968', label: '+968 (Oman)' },
];

const countries: DropdownOption[] = [
  { value: 'Kuwait', label: 'Kuwait' },
  { value: 'Saudi Arabia', label: 'Saudi Arabia' },
  { value: 'UAE', label: 'United Arab Emirates' },
  { value: 'Qatar', label: 'Qatar' },
  { value: 'Bahrain', label: 'Bahrain' },
  { value: 'Oman', label: 'Oman' },
];

const citiesByCountry: Record<string, DropdownOption[]> = {
  'Kuwait': [
    { value: 'Kuwait City', label: 'Kuwait City' },
    { value: 'Hawalli', label: 'Hawalli' },
    { value: 'Farwaniya', label: 'Farwaniya' },
    { value: 'Ahmadi', label: 'Ahmadi' },
    { value: 'Jahra', label: 'Jahra' },
    { value: 'Mubarak Al-Kabeer', label: 'Mubarak Al-Kabeer' },
  ],
  'Saudi Arabia': [
    { value: 'Riyadh', label: 'Riyadh' },
    { value: 'Jeddah', label: 'Jeddah' },
    { value: 'Mecca', label: 'Mecca' },
    { value: 'Medina', label: 'Medina' },
    { value: 'Dammam', label: 'Dammam' },
    { value: 'Khobar', label: 'Khobar' },
  ],
  'UAE': [
    { value: 'Dubai', label: 'Dubai' },
    { value: 'Abu Dhabi', label: 'Abu Dhabi' },
    { value: 'Sharjah', label: 'Sharjah' },
    { value: 'Ajman', label: 'Ajman' },
    { value: 'Ras Al Khaimah', label: 'Ras Al Khaimah' },
    { value: 'Fujairah', label: 'Fujairah' },
  ],
  'Qatar': [
    { value: 'Doha', label: 'Doha' },
    { value: 'Al Rayyan', label: 'Al Rayyan' },
    { value: 'Al Wakrah', label: 'Al Wakrah' },
    { value: 'Al Khor', label: 'Al Khor' },
    { value: 'Umm Salal', label: 'Umm Salal' },
  ],
  'Bahrain': [
    { value: 'Manama', label: 'Manama' },
    { value: 'Muharraq', label: 'Muharraq' },
    { value: 'Riffa', label: 'Riffa' },
    { value: 'Hamad Town', label: 'Hamad Town' },
    { value: 'Isa Town', label: 'Isa Town' },
  ],
  'Oman': [
    { value: 'Muscat', label: 'Muscat' },
    { value: 'Salalah', label: 'Salalah' },
    { value: 'Sohar', label: 'Sohar' },
    { value: 'Nizwa', label: 'Nizwa' },
    { value: 'Sur', label: 'Sur' },
  ],
};

// Country to code mapping
const countryToCode: Record<string, string> = {
  'Kuwait': '+965',
  'Saudi Arabia': '+966',
  'UAE': '+971',
  'Qatar': '+974',
  'Bahrain': '+973',
  'Oman': '+968',
};

const GarageRegister: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    countryCode: '965',
    phoneNumber: '',
    garageName: '',
    garageAddress: '',
    country: 'Kuwait',
    city: ''
  });

  // Update country code when country changes
  const handleCountryChange = (country: string) => {
    setFormData(prev => ({
      ...prev,
      country,
      countryCode: countryToCode[country] || '+965',
      city: '' // Reset city when country changes
    }));
  };

  // Format phone number to remove any country code if entered
  const formatPhoneNumber = (value: string) => {
    // Remove any non-digit characters
    let cleaned = value.replace(/\D/g, '');
    
    // Remove country code if present
    if (cleaned.startsWith('965')) {
      cleaned = cleaned.substring(3);
    }
    
    return cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.city) {
      setError('Please select a city');
      return;
    }

    setLoading(true);

    try {
      // Format phone number without country code
      const phoneWithoutCode = formatPhoneNumber(formData.phoneNumber);
      
      const registrationData = {
        name: `${formData.firstName} ${formData.lastName}`, // Combine for backend compatibility
        email: formData.email,
        password: formData.password,
        phoneNumber: phoneWithoutCode, // Send without country code
        garageName: formData.garageName,
        garageAddress: formData.garageAddress,
        city: formData.city,
        country: formData.country
      };

      await authService.garageRegister(registrationData);
      
      // Registration successful - redirect to login with success message
      navigate('/login/garage', { 
        state: { 
          message: 'Registration successful! Please log in with your credentials.',
          type: 'success'
        }
      });
    } catch (err: any) {
      console.error('Registration error:', err);
      
      // Handle specific error messages
      if (err.response?.status === 409 || err.response?.data?.error?.includes('already exists')) {
        setError('An account with this email or phone number already exists. Please use different credentials or try logging in.');
      } else if (err.response?.status === 500 && err.response?.data?.error?.includes('foreign key constraint')) {
        setError('Registration is temporarily unavailable. Please try again later or contact support.');
      } else {
        setError(err.response?.data?.error || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/">
                <h1 className="text-2xl font-bold" style={{ color: '#008B8B' }}>Rayyi7</h1>
              </Link>
            </div>

            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-8">
                <Link to="/#features" className="text-gray-700 hover:text-[#008B8B] transition">
                  {t('nav.features')}
                </Link>
                <Link to="/#about" className="text-gray-700 hover:text-[#008B8B] transition">
                  {t('nav.about')}
                </Link>
                <Link to="/pricing" className="text-gray-700 hover:text-[#008B8B] transition">
                  {t('nav.pricing')}
                </Link>
              </div>

              <LanguageToggle />

              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-gray-700" />
                ) : (
                  <Menu className="h-6 w-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <Link 
                to="/#features" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.features')}
              </Link>
              <Link 
                to="/#about" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.about')}
              </Link>
              <Link 
                to="/pricing" 
                className="block text-gray-700 hover:text-[#008B8B] transition py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.pricing')}
              </Link>
            </div>
          </div>
        )}
      </nav>

      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-64px)]">
        <div className="max-w-md w-full">
          {/* Back Button */}
          <button
            onClick={() => navigate('/login/garage')}
            className="mb-8 flex items-center text-gray-600 hover:text-[#008B8B] transition"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Login
          </button>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="text-center mb-8">
              <div className="bg-[#008B8B] bg-opacity-10 p-4 rounded-full inline-block mb-4">
                <Building2 className="w-12 h-12" style={{ color: '#008B8B' }} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Register Your Garage</h2>
              <p className="text-gray-600 mt-2">Join the Rayyi7 platform and grow your business</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
                </div>
              )}

              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition"
                    placeholder="your@email.com"
                  />
                </div>

                <PhoneInput
                  label="Phone Number"
                  countryCode={formData.countryCode}
                  phoneNumber={formData.phoneNumber}
                  onCountryCodeChange={(code) => setFormData({ ...formData, countryCode: code })}
                  onPhoneNumberChange={(number) => setFormData({ ...formData, phoneNumber: number })}
                  required
                  className="mb-4"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Garage Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Garage Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Garage Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.garageName}
                    onChange={(e) => setFormData({ ...formData, garageName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition"
                    placeholder="Enter your garage name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={countries}
                    value={formData.country}
                    onChange={handleCountryChange}
                    placeholder="Select country"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={citiesByCountry[formData.country] || []}
                    value={formData.city}
                    onChange={(value) => setFormData({ ...formData, city: value })}
                    placeholder="Select city"
                    disabled={!formData.country}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Garage Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.garageAddress}
                    onChange={(e) => setFormData({ ...formData, garageAddress: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent transition"
                    placeholder="Enter garage street address"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#008B8B] text-white py-3 rounded-lg hover:bg-[#007A7A] transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login/garage" className="text-[#008B8B] hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GarageRegister;